import React, { useEffect } from 'react';

import { useAuth } from '../../hooks/useAuth.jsx';
import { useUserDataContext } from '../../hooks/useUserDataContext.jsx';
import { isPatient } from '../../utils';
import { IntercomContext } from './IntercomContext.ts';
import { useInternalIntercom } from './useInternalIntercom.ts';

type IntercomProviderProps = {
  children: React.ReactNode;
};

export const IntercomProvider = ({ children }: IntercomProviderProps) => {
  const { user, userType } = useAuth();
  const { userData } = useUserDataContext();
  const shouldBoot = Boolean(user && userType && isPatient(userType));
  const intercom = useInternalIntercom(shouldBoot);

  // Identify user when patient is logged in and userData is available
  useEffect(() => {
    if (!intercom.isAvailable) {
      return;
    }

    if (!user || !userType || !isPatient(userType)) {
      return;
    }

    if (!userData?.email) {
      return;
    }

    intercom.update({
      user_id: userData.canview?.userId ?? user.uid,
      email: userData.email,
      name: [userData.firstName, userData.lastName].filter(Boolean).join(' ') || undefined,
      phone: userData.shipping?.phone || undefined,
      Platform: 'candor',
      'User Type': userType,
      candor_user_id: user.uid,
    });
  }, [
    intercom.isAvailable,
    user?.uid,
    userType,
    userData?.email,
    userData?.firstName,
    userData?.lastName,
    userData?.canview?.userId,
  ]);

  // Shutdown Intercom when shouldBoot becomes false (logout or role change)
  useEffect(() => {
    if (!intercom.isAvailable) {
      return;
    }

    if (!shouldBoot) {
      intercom.shutdown();
    }
  }, [intercom.isAvailable, shouldBoot]);

  return <IntercomContext.Provider value={intercom}>{children}</IntercomContext.Provider>;
};
